import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Whatshot as WhatshotIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

// AdsFather-themed taglines for the login page
const LOGIN_TAGLINES = [
  // Classic Godfather lines adapted to ads
  "Respect is earned. Clicks are bought.",
  "I'm gonna make you an offer you can't refuse... 30% off your first SP Subscription!!",
  "Keep your friends close, your ads closer.",
  "It's not personal, it's strictly business... and Impact Velocity.",
  "Leave the gun, take the analytics.",
  "In this family, we measure twice and advertise once.",
  "You come to me on the day of your product launch...",
  "I didn't choose the ad life, the ad life chose me.",
  "Sleeping with the fishes? More like swimming in conversions.",
  "The Ads Father: Making offers audiences can't refuse since 2025.",
  "Family first, LTV second.",
  "We don't break kneecaps, we break through to customers.",
  "Loyalty is everything. Brand loyalty is profitable.",
  "Never go against the family... brand guidelines.",
  "A man who doesn't spend time with his campaigns will never have good ROI.",
  "It's not about the money, it's about sending a message... to your target audience.",
  "The clicks will flow like wine at a wedding.",
  "Keep your enemies closer, your competitors' ads closest.",
  "Don't ever take sides with anyone against the family... or the algorithm.",
  "The richest man is the one with the most loyal customers.",
  "Never hate your enemies. It affects your judgment... and your ad spend.",
  "I have a sentimental weakness for my campaigns.",
  "Someday, and that day may never come, I'll call upon you for a favor... a 5-star review.",
  "Justice is a dish best served... with proper attribution.",
  "I'm not a monster. I'm just ahead of the conversion window.",
  "The man who moves a mountain begins by carrying away small rocks... and testing small budgets.",
  "I'm gonna make you a campaign you can't pause.",
  "The best revenge is massive campaign success.",
  "If anything in this life is certain, it's that clicks can be bought.",

  // Video Upload & Ad Creation
  "In my family, we don't upload just any video... we upload masterpieces.",
  "Your video file sleeps with the fishes? More like it swims in our upload queue.",
  "I believe in America. And uploading your ads to the right platforms.",
  "A video without a thumbnail is like a man without a face... invisible.",
  "Never let no one know what you think... except your ad creative team.",
  "In this family, we respect the upload button... and fear the rejection email.",
  "The saddest thing in life is wasted ad spend... the second is a failed upload.",
  "When you upload, you upload for keeps... or until the campaign ends.",
  "I don't upload videos. I upload opportunities.",
  "Your video is nothing without the right targeting... and neither are you.",

  // Campaign Management
  "In my world, every campaign has a godfather... and it's me.",
  "You launch a campaign, you launch for life... or until the budget runs out.",
  "The best campaign is the one that converts... like a good confession.",
  "Campaign management is like making wine... it takes time and patience.",
  "A campaign without A/B testing is like a family without loyalty.",
  "The family that optimizes together... conquers the market together.",
  "Don't ever take sides against your own campaign... optimize it instead.",
  "A successful campaign is built on a mountain of failed tests.",

  // Ad Performance & Analytics
  "Show me the money... and the 1dashboard.",
  "Analytics don't lie... but sometimes they need interpretation.",
  "In this family, we don't do impressions... we do conversions.",
  "Never hate your competitors. Study their ad performance instead.",
  "I believe in America... and attribution models.",
  "The saddest thing in life is a campaign with zero conversions.",
  "Power wears out those who don't track their performance.",

  // Budget Management
  "A dollar saved is a dollar earned... but a well-spent dollar is priceless.",
  "Budget control is the new gun control... handle with care.",
  "I don't like overspend. I'm a businessman. Wasted budget is a big expense.",
  "A campaign without budget discipline is like a family without respect.",
  "I have a sentimental weakness for profitable campaigns.",

  // Targeting & Audiences
  "Keep your friends close, your target audience closer.",
  "You can target anyone, but you can't target everyone... that's spam.",
  "In this family, we don't guess... we target with precision.",
  "Your audience is nothing without the right message... and neither are you.",
  "Audiences don't die... they just get retargeted.",
  "The saddest thing in life is wasted impressions.",

  // Creative Management
  "The best creative is built on a mountain of failed variants.",
  "Your ad copy is nothing without the right hook... and neither are you.",
  "Creatives don't die... they get A/B tested to death.",
  "In my world, every creative has a godfather... and it's data.",
  "The family that creates together... converts together.",

  // Platform Management
  "YouTube, Meta, Google... all bow to the Ads Father.",
  "In this family, we don't pick platforms... we conquer them.",
  "Never go against the algorithm... unless you have better data.",
  "Your platform strategy is nothing without the right targeting.",
  "Platforms don't die... they get updated and you adapt.",
  "Tell your APIs they're going to the mattresses... of our integration layer.",

  // Conversion Tracking
  "I believe in America... and proper attribution.",
  "Never hate your analytics. Study them and optimize.",
  "The best revenge is a perfectly attributed conversion.",
  "I don't track conversions... I track opportunities.",

  // Ad Operations & Workflow
  "I don't manage ads... I manage empires of conversions.",
  "The saddest thing in life is manual ad management.",

  // Final Collection of Classics with Ad Twist
  "The Ads Father doesn't upload videos... he uploads legacies.",
  "In my campaigns, there are no accidents... only optimization opportunities.",
  "I have but one campaign to sell to you... and it's performing beautifully."
];

const LoginPage: React.FC = () => {
  const { login, loading } = useAuth();
  const [currentTagline, setCurrentTagline] = useState('');

  useEffect(() => {
    // Set a random tagline on component mount
    setCurrentTagline(LOGIN_TAGLINES[Math.floor(Math.random() * LOGIN_TAGLINES.length)]);
  }, []);

  return (
    <Box
      sx={{
        minHeight: { xs: '70vh', md: '75vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 5 },
            textAlign: 'center',
            borderRadius: 4,
            background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative corner elements */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              opacity: 0.1,
              transform: 'rotate(-15deg)',
            }}
          >
            <WhatshotIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              opacity: 0.1,
              transform: 'rotate(15deg)',
            }}
          >
            <WhatshotIcon sx={{ fontSize: 32 }} />
          </Box>

          {/* Logo Section */}
          <Box sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                fontSize: { xs: '2.2rem', sm: '2.8rem' },
              }}
            >
              TheAdsFather
            </Typography>

            {/* Animated tagline */}
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              minHeight: '2.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                animation: 'fadeIn 0.5s ease-in',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(10px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              "{currentTagline}"
            </Typography>
          </Box>

          {/* Centered Login Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={login}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  <GoogleIcon sx={{ fontSize: 24 }} />
                )
              }
              sx={{
                py: 2,
                px: 6,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                borderRadius: 3,
                textTransform: 'none',
                minWidth: { xs: '260px', sm: '320px' },
                background: loading
                  ? '#666'
                  : 'linear-gradient(45deg, #4285f4 30%, #34a853 90%)',
                boxShadow: '0 8px 32px rgba(66, 133, 244, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: loading
                    ? '#666'
                    : 'linear-gradient(45deg, #3367d6 30%, #2d8f47 90%)',
                  boxShadow: '0 12px 40px rgba(66, 133, 244, 0.4)',
                  transform: loading ? 'none' : 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              {loading ? 'Making you an offer...' : 'Sign in with Google'}
            </Button>
          </Box>

          {/* Subtle footer text */}
          <Typography
            variant="caption"
            sx={{
              mt: 4,
              color: 'text.secondary',
              opacity: 0.7,
              fontSize: '0.8rem',
            }}
          >
            Organization access only • @hellosimply.com
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage; 